import React, { ComponentProps, ReactNode } from 'react'
import { ControllerProps, ControllerRenderProps, useController } from 'react-hook-form'

import { bemJoin } from 'bem-join'
import classnames from 'classnames'

import { FormField } from '@app/lib/display/FormField/FormField'
import { VisuallyHidden } from '@app/lib/display/VisuallyHidden/VisuallyHidden'
import { MergeProps } from '@app/lib/types'

import './InputField.scss'

export type InputFieldProps = MergeProps<
  ControllerProps,
  {
    label?: ReactNode | ((props: ComponentProps<'label'>) => ReactNode)
    hideLabel?: boolean
    description?: ReactNode
    loading?: boolean
    render: (args: { inputProps: ControllerRenderProps; error?: boolean }) => ReactNode
    className?: string
    disabled?: boolean
  }
>

const b = bemJoin('input-field')

export const InputField = ({
  label,
  description,
  render,
  loading,
  className,
  disabled,
  hideLabel,
  ...controllerProps
}: InputFieldProps) => {
  const {
    field,
    fieldState: { error }
  } = useController(controllerProps)

  const Label = (labelProps: ComponentProps<any>) => {
    if (typeof label === 'function') {
      return label(labelProps)
    }

    return <label {...labelProps}>{label}</label>
  }

  return (
    <FormField
      label={!!label}
      description={!!description}
      error={!!error}
      className={classnames(
        b({
          invalid: !!error,
          loading: loading
        }),
        className
      )}
      render={({ fieldProps, labelProps, inputProps, descriptionProps, errorMessageProps }) => (
        <div {...fieldProps}>
          {hideLabel ? (
            <VisuallyHidden>
              <Label {...labelProps} className={b('label')} />
            </VisuallyHidden>
          ) : (
            <Label {...labelProps} className={b('label')} />
          )}
          {render({
            inputProps: {
              ...inputProps,
              ...field,
              disabled,
              className: b('input', {
                invalid: !!error,
                loading: loading
              })
            },
            error: !!error
          })}
          {description && (
            <div {...descriptionProps} className={b('description')}>
              {description}
            </div>
          )}
          {error && (
            <div {...errorMessageProps} className={b('error')}>
              {error.message}
            </div>
          )}
        </div>
      )}
    />
  )
}
